import { gsap } from 'gsap';
import { checkGameAccess } from './auth/gameGate';
import { loadAssetOverrides } from './assets/overridesSource';
import { THE_PIT } from './arenas';
import { resolveBattle } from './battle/BattleEngine';
import type { BattleSequence } from './battle/types';
import { SPARKY_BOT } from './characters';
import { BATTLE_PACE_SCALE } from './data/constants';
import { buildScenario } from './dev/scenario';
import { isDebugMode, setDebugMode } from './dev/debugMode';
import { SKILL_BUNDLES, SKILLS } from './skills';
import type { LoadedSkillAssets } from './skills/types';
import { BattleRenderer } from './render/BattleRenderer';
import { BattleScene } from './render/BattleScene';
import { FilmEffect, type FilmMode } from './render/FilmEffect';
import { PlaceholderAdapter } from './render/adapters/PlaceholderAdapter';
import {
  TITLE_FONT_FAMILY,
  UI_FONT_FAMILY,
  loadArena,
  loadCharacterAnimations,
  loadCharacterParticles,
  loadCharacterSfx,
  loadSkillAssets,
  loadTitleFont,
  loadUiTextures,
} from './render/assets';
import {
  computeDesignViewport,
  computeTextResolution,
  createPixiApp,
  detectLayoutMode,
} from './render/layout';
import { BattleSetupModal } from './screens/BattleSetupModal';
import { DesktopScreen } from './screens/DesktopScreen';
import type { EnemyCardData } from './screens/EnemyCard';
import type { ScriptIcons } from './screens/ScriptCell';
import type { SkillSlotData } from './screens/SkillSlot';

const CURSOR_URL = '/assets/ui/cursor.png';

async function bootstrap(): Promise<void> {
  // Gate the game behind the same login as the studio: only allowlisted,
  // signed-in accounts may play. Runs before any Pixi/asset work.
  const access = await checkGameAccess();
  if (access === 'login') {
    window.location.replace('/studio');
    return;
  }
  if (access === 'forbidden') {
    showAccessDenied();
    return;
  }

  // Everyone who reaches the game today is a signed-in, allowlisted designer
  // (came through /studio login), so turn debug tooling on for them (ESC-quit,
  // script builder). Future non-designer players (Steam token etc.) will not.
  // An explicit ?debug=0/1 still wins.
  const debugForced = new URLSearchParams(window.location.search).has('debug');
  if (access === 'ok' && !debugForced) setDebugMode(true);

  // Slow down all GSAP tweens (attack/dodge/hit/etc + floating text).
  gsap.globalTimeline.timeScale(1 / BATTLE_PACE_SCALE);

  const mode = detectLayoutMode();
  const app = await createPixiApp(mode);
  // Sync Pixi ticker (drives AnimatedSprite frame stepping) with the same pace
  // scale we use for GSAP. Otherwise sprite anims play at full speed while
  // tweens are slowed.
  app.ticker.speed = 1 / BATTLE_PACE_SCALE;

  // Custom hand cursor everywhere on the canvas (same art for default and
  // pressable-element hover - hover states are not used, only pressed).
  app.renderer.events.cursorStyles.default = `url('${CURSOR_URL}') 6 2, auto`;
  app.renderer.events.cursorStyles.pointer = `url('${CURSOR_URL}') 6 2, pointer`;

  // Fetch designer asset overrides BEFORE loading assets, so uploaded sprites
  // resolve in place of the bundled defaults. No-op (and instant) when Firebase
  // is not configured - the loaders then use bundled paths.
  await loadAssetOverrides();

  const [arena, sparkyAnimations, sparkySfx, sparkyParticles, skillAssetsList, ui] =
    await Promise.all([
      loadArena(THE_PIT),
      loadCharacterAnimations(SPARKY_BOT),
      loadCharacterSfx(SPARKY_BOT),
      loadCharacterParticles(SPARKY_BOT),
      Promise.all(
        [...SKILL_BUNDLES.entries()].map(async ([id, bundle]) => {
          return [id, await loadSkillAssets(bundle)] as const;
        }),
      ),
      loadUiTextures(),
      loadTitleFont(),
    ]);
  const skillAssets: ReadonlyMap<string, LoadedSkillAssets> = new Map(skillAssetsList);
  const textResolution = computeTextResolution(mode);

  // Old-film post effect (grain + flicker + vignette + scratches).
  // OLD_FILM_EFFECT: false turns it off. FILM_MODE: 'color' keeps the
  // original colours (faded film), 'sepia' = warm brown old-B&W look.
  const OLD_FILM_EFFECT = false;
  const FILM_MODE: FilmMode = 'color';

  const skillIcon = (id: string) => skillAssets.get(id)?.textures.logIcon;

  // Demo opponent for the Fights tab.
  const demoEnemy: EnemyCardData = {
    title: 'Enemy 1:',
    name: 'SparkyBot',
    level: 5,
    avatar: ui.enemyAvatar,
    skills: [
      { icon: skillIcon('cannon'), label: 'Cannon' },
      { icon: skillIcon('charge'), label: 'Charge' },
      { icon: skillIcon('repair'), label: 'Repair' },
      { icon: skillIcon('sparky_bunker'), label: 'Bunker' },
      { icon: skillIcon('cannon'), label: 'Cannon' },
      { icon: skillIcon('charge'), label: 'Charge' },
    ],
  };

  // Player's own skills for the Skills tab - SparkyBot's loadout, in skillIds
  // order, limited to skills that have a ui icon (earthshake has none yet).
  const playerSkills: SkillSlotData[] = SPARKY_BOT.config.skillIds
    .filter((id) => skillIcon(id) !== undefined)
    .map((id) => ({ icon: skillIcon(id), label: SKILLS.get(id)?.name ?? id }));

  // Action icons for the battle-script builder. punch/kick are not skills (loaded
  // via ui); the rest reuse the skill log icons. charge is the buff-toggle icon.
  const scriptIcons: ScriptIcons = {
    punch: ui.punchIcon,
    kick: ui.kickIcon,
    cannon: skillIcon('cannon'),
    earthshake: skillIcon('earthshake'),
    repair: skillIcon('repair'),
    charge: skillIcon('charge'),
  };

  let battleRunning = false;
  // The renderer of the in-progress battle, for debug-mode ESC-to-quit.
  let activeRenderer: BattleRenderer | null = null;

  const desktop = new DesktopScreen({
    ui,
    enemies: [demoEnemy],
    playerSkills,
    fontFamily: UI_FONT_FAMILY,
    textResolution,
    viewport: () => computeDesignViewport(mode),
    // Debug mode: open the script builder. Otherwise just run a random battle.
    onFightSelected: () => {
      if (isDebugMode()) battleSetup.show();
      else void playSequence(buildSequence({}));
    },
  });
  app.stage.addChild(desktop);

  // Pre-battle setup overlay (above the desktop). Persistent so a tester's
  // script survives across opens. Hidden until SELECT.
  const battleSetup = new BattleSetupModal({
    icons: scriptIcons,
    selectBtn: ui.selectBtn,
    border: ui.border,
    corner: ui.corner,
    viewport: () => computeDesignViewport(mode),
    fontFamily: UI_FONT_FAMILY,
    textResolution,
    onRandom: () => {
      battleSetup.visible = false;
      void playSequence(buildSequence({}));
    },
    onFight: (p1Spec, p2Spec) => {
      let sequence: BattleSequence;
      try {
        const sc = buildScenario(p1Spec, p2Spec, {
          p1: SPARKY_BOT.config,
          p2: SPARKY_BOT.config,
          skills: SKILLS,
        });
        sequence = buildSequence({ rng: sc.rng, rounds: sc.rounds });
      } catch (err) {
        // buildScenario / strict rng reject impossible scripts (e.g. R at full
        // HP, AB without a block skill) - surface it, keep the modal open.
        battleSetup.showError(err instanceof Error ? err.message : 'Invalid battle script');
        return;
      }
      battleSetup.visible = false;
      void playSequence(sequence);
    },
    onClose: () => {
      battleSetup.visible = false;
    },
  });
  battleSetup.visible = false;
  app.stage.addChild(battleSetup);

  // Debug-mode ESC: quit an in-progress battle (back to the Fights tab), or close
  // the setup modal if it's open.
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !isDebugMode()) return;
    if (battleRunning) activeRenderer?.cancel();
    else if (battleSetup.visible) battleSetup.visible = false;
  });

  void desktop.playEnter();

  // Resolves a battle for the SparkyBot mirror on The Pit. No rng -> Math.random
  // (random battle); no rounds -> ROUNDS_PER_BATTLE.
  function buildSequence(params: { rng?: () => number; rounds?: number }): BattleSequence {
    return resolveBattle({
      p1: SPARKY_BOT.config,
      p2: SPARKY_BOT.config,
      skills: SKILLS,
      arenaInteractions: arena.bundle.interactions,
      ...(params.rng ? { rng: params.rng } : {}),
      ...(params.rounds !== undefined ? { rounds: params.rounds } : {}),
    });
  }

  // Plays a pre-resolved battle sequence full-screen, then returns to desktop.
  async function playSequence(sequence: BattleSequence): Promise<void> {
    if (battleRunning) return;
    battleRunning = true;
    desktop.visible = false;

    const scene = new BattleScene({
      players: sequence.players,
      textResolution,
      arena,
      characterAnimations: { p1: sparkyAnimations, p2: sparkyAnimations },
      characterSfx: { p1: sparkySfx, p2: sparkySfx },
      characterTimings: { p1: SPARKY_BOT.timings, p2: SPARKY_BOT.timings },
      characterParticles: { p1: sparkyParticles, p2: sparkyParticles },
      titleFontFamily: TITLE_FONT_FAMILY,
    });
    app.stage.addChild(scene);

    if (OLD_FILM_EFFECT) {
      new FilmEffect(scene, app.ticker, FILM_MODE).start();
    }

    const renderer = new BattleRenderer(scene, new PlaceholderAdapter(), {
      bundles: SKILL_BUNDLES,
      assets: skillAssets,
    });
    activeRenderer = renderer;
    await renderer.play(sequence);
    activeRenderer = null;

    // On an aborted battle, fire-and-forget tweens (screen shake, floating
    // text, arena drops) may still be live - kill them before teardown so their
    // callbacks don't touch the destroyed scene.
    if (renderer.cancelled) gsap.globalTimeline.clear();

    app.stage.removeChild(scene);
    scene.destroy({ children: true });
    desktop.visible = true;
    if (renderer.cancelled) desktop.showFightsTab();
    battleRunning = false;
  }
}

// Shown when a signed-in account is NOT on the designers allowlist. Plain DOM
// overlay (the game canvas never starts in this case).
function showAccessDenied(): void {
  const el = document.createElement('div');
  el.style.cssText =
    'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;' +
    'justify-content:center;gap:12px;background:#1a1410;color:#e8dcc4;' +
    'font-family:Roboto,system-ui,sans-serif;text-align:center;padding:24px;';
  const h = document.createElement('h1');
  h.textContent = 'No access';
  const p = document.createElement('p');
  p.textContent = 'This account is not allowed to play. Ask an admin for access.';
  const a = document.createElement('a');
  a.href = '/studio';
  a.textContent = 'Go to studio';
  a.style.cssText = 'color:#ffd000;';
  el.append(h, p, a);
  document.body.appendChild(el);
}

bootstrap().catch((err: unknown) => {
  console.error('Bootstrap failed', err);
});
